import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { PiWarningCircleBold } from 'react-icons/pi';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

export type AiFeatures = { documents: boolean; shipments: boolean; stock: boolean; commission: boolean; purchase_orders: boolean };

export function useAiStatus() {
    return useQuery({
        queryKey: ['ai', 'status'],
        staleTime: 10 * 60 * 1000,
        queryFn: () => api.get<{ data: { enabled: boolean; features: AiFeatures } }>('/ai/status').then((r) => r.data.data),
    });
}

export const aiErrorMessage = (err: unknown) =>
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'A IA não conseguiu responder agora. Tente de novo.';

/** Ícone da IA do Hub: a anilha Buckler. `spinning` = trabalhando. */
export function AiPlateIcon({ className, spinning = false }: { className?: string; spinning?: boolean }) {
    return (
        <img
            src="/img/ai/buckler-plate-64.webp"
            srcSet="/img/ai/buckler-plate-64.webp 1x, /img/ai/buckler-plate-128.webp 2x"
            alt=""
            aria-hidden="true"
            draggable={false}
            className={cn('inline-block shrink-0 select-none', spinning && 'animate-spin [animation-duration:1.4s] motion-reduce:animate-none', className)}
        />
    );
}

/** Botão padrão das ações de IA (sugestão, resumo, análise). */
export const AI_BUTTON =
    'inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white font-semibold text-gray-900 shadow-sm transition hover:border-rose-300 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-500/30 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-rose-500/10';

/** Estado de espera: a anilha girando. A resposta leva de 5 a 30 s. */
export function AiThinking({ label = 'A IA está analisando…', hint = 'Leva de 5 a 30 segundos.' }: { label?: string; hint?: string }) {
    return (
        <div className="flex flex-col items-center gap-3 py-10 text-center" role="status" aria-live="polite">
            <img
                src="/img/ai/buckler-plate-128.webp"
                srcSet="/img/ai/buckler-plate-128.webp 1x, /img/ai/buckler-plate-512.webp 3x"
                alt=""
                aria-hidden="true"
                draggable={false}
                className="size-20 select-none animate-spin [animation-duration:1.6s] drop-shadow-[0_8px_16px_rgba(220,38,38,0.25)] motion-reduce:animate-none"
            />
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>
        </div>
    );
}

export function AiBadge({ className }: { className?: string }) {
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 rounded-full border border-rose-200 bg-white px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-800 dark:border-rose-500/30 dark:bg-gray-900 dark:text-gray-100',
                className,
            )}
        >
            <AiPlateIcon className="size-3.5" /> IA
        </span>
    );
}

export function AiDisclaimer({ generatedAt, cached, children }: { generatedAt?: string; cached?: boolean; children?: ReactNode }) {
    return (
        <p className="flex flex-wrap items-center gap-1.5 text-[11px] text-gray-400">
            <PiWarningCircleBold className="size-3.5" />
            Gerado por IA a partir dos dados do Hub — confira antes de agir.
            {generatedAt && <span>· {cached ? 'do cache, ' : ''}gerado em {generatedAt}</span>}
            {children}
        </p>
    );
}

export function AiError({ message, onRetry }: { message: string; onRetry?: () => void }) {
    return (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-6 text-center text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200">
            {message}
            {onRetry && (
                <button type="button" onClick={onRetry} className="text-xs font-semibold underline">
                    Tentar de novo
                </button>
            )}
        </div>
    );
}
